import {
  createEffect,
  createSignal,
  createUniqueId,
  DEV,
  For,
  onMount,
  Show,
} from "solid-js";
import { createStore } from "solid-js/store";
import type {
  InputOption,
  InputTypeRadioGroupProps,
} from "~/components/Forms/Fields/Base/InputTypesProps";
import { useFormContext } from "~/contexts/FormContext";

export function InputTypeRadioGroup(props: InputTypeRadioGroupProps) {
  const [, { setValue: formSetValue }] = useFormContext();
  const setValue = props.defaultFormStorage
    ? props.defaultFormStorage
    : formSetValue;
  const isRequired = () => props.required || false;

  // eslint-disable-next-line solid/reactivity
  const id = `${props.name}-${createUniqueId()}`;

  let input: HTMLInputElement | undefined;

  const [activeOptions, setActiveOptions] = createStore<InputOption[]>([]);

  // eslint-disable-next-line solid/reactivity
  const [inputHasContent, setInputHasContent] = createSignal(!!props.value);
  const [inputIsFocus, setInputIsFocus] = createSignal(false);
  const [touched, setTouched] = createSignal(false);

  onMount(() => {
    props.validateFn && props.value && props.validateFn(props.value);
    setInputHasContent(!!props.value);
  });

  createEffect(() => {
    if (activeOptions.length > 0) {
      setValue!(props.name, activeOptions.map((o) => o.value).join(","));
      setInputHasContent(true);
    } /* else if (initialValue && !props.multiple) {
      const opt = props.options.find((o) => o.value === initialValue);

      setActiveOptions([opt]);
      setInputHasContent(true);
    } else if (initialValue && props.multiple) {
      const values: string[] = (initialValue as string).split(",");

      const options = props.options.filter((o) => values.includes(o.value));
      setActiveOptions(options);

      setInputHasContent(true);
      initialValue = undefined;
    } else {
      props.setFields(props.name, null);
      setInputHasContent(false);
    } */

    /* if (typeof props.onChange === "function" && activeOptions.length > 0) {
      props.onChange(activeOptions);
    } */
  });

  function setActiveOptionFromUI(option: InputOption) {
    DEV && console.debug("Option selected", option);

    // FIXME that's not possible to have multiple options selected in a radio group.
    if (!props.multiple) {
      setActiveOptions([option]);
    } else {
      // Remove option if already in the selected set
      if (activeOptions.find((o) => o.value === option.value)) {
        const newOptions = activeOptions.filter(
          (o) => o.value !== option.value,
        );
        setActiveOptions(newOptions);
      } else {
        // Otherwise add it.
        setActiveOptions((opts: InputOption[]) => [
          // We use a Set because we don't want to have duplicates.
          ...new Set([...opts, option]),
        ]);
      }
    }

    // Now that we allowed external hooks to be executed, we can check for disabled options.
    setActiveOptions((opts: InputOption[]) => [
      // We use a Set because we don't want to have duplicates.
      ...new Set([...opts.filter((o) => !o.disabled)]),
    ]);

    if (props.validateFn && touched()) {
      DEV && console.log("validateFn", activeOptions);
      props.validateFn(activeOptions[0]?.value ?? "");
    }
  }

  return (
    <>
      <div
        class={`form-control form-control-${props.name}`}
        classList={{
          "is-focus": inputIsFocus(),
          "has-content": inputHasContent(),
          "in-error": props["aria-invalid"],
          "is-disabled": props.disabled,
        }}
        id={id}
      >
        <div class="form-radio-group">
          <Show when={props.label} keyed>
            <label for={props.name}>
              {props.label}
              <Show when={isRequired()} keyed>
                {" "}
                *
              </Show>
            </label>
          </Show>

          <span class="radio-group">
            <For each={props.options}>
              {(option) => {
                const id = `${props.name}-${option.name}-${createUniqueId()}`;
                return (
                  <>
                    <div class="radio-control">
                      <input
                        type="radio"
                        id={id}
                        data-test={`${props.name}-${option.name}`}
                        name={props.name}
                        required={isRequired()}
                        aria-required={isRequired()}
                        onClick={() => {
                          setInputIsFocus(true);
                          setTouched(true);
                          if (!option.disabled) {
                            setActiveOptionFromUI(option);
                          }
                        }}
                        value={option.value}
                        checked={option.value === props.value}
                      />

                      <label for={id}>{option.label}</label>
                    </div>
                  </>
                );
              }}
            </For>
          </span>
        </div>
        <Show when={props.help} keyed>
          <div class="field-help">{props.help}</div>
        </Show>
      </div>
    </>
  );
}
